import { defineStore } from "pinia";
import { computed } from "vue";
import { pickBy } from "lodash-es";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import type { SceneNodeItem } from "../scene/SceneNodeItem";
import type { SceneMediumData } from "./sceneMediumData";
import { useMediaStore } from "@core/media/media.store";
import type { Subset } from "@/helpers/utility/Type";
import type { Uid } from "@/helpers/utility/Id";

export const useSceneMediumStore = defineStore("model/sceneMedia", () => {
  const sceneNodeStore = useSceneNodesStore();

  function isMediumItem(item: SceneNodeItem): item is SceneNodeItem<"medium"> {
    return item.dataType === "medium";
  }

  async function create(options: Subset<SceneNodeItem<"medium">>, data: Partial<SceneMediumData> = {}) {
    const filledData: SceneMediumData = {
      ...options.data,
      ...data,
    };
    return await sceneNodeStore.create({
      aspectRatioLocked: true,
      ...options,
      dataType: "medium",
      data: filledData,
    });
  }

  const items = computed(() => {
    return pickBy(sceneNodeStore.items, isMediumItem) as Record<number, SceneNodeItem<"medium">>;
  });

  const byUid = computed(() => {
    return pickBy(sceneNodeStore.byUid, isMediumItem) as Record<string, SceneNodeItem<"medium">>;
  });

  const mediaData = computed(() => {
    const mediaDataStore = useMediaStore().data;
    const entries: { [mediumUid: Uid<"sceneNode">]: string | null } = {};
    for (const id in items.value) {
      const item = items.value[id];
      const mediaUuid = item.data.mediaUUID;
      if (mediaUuid == null) {
        entries[item.uid] = null;
      } else {
        entries[item.uid] = mediaDataStore?.[mediaUuid];
      }
    }
    return entries;
  });

  const linkedMediaUUIDs = computed(() => {
    return new Set(
      Object.values(items.value)
        .map((item) => (item.data as SceneMediumData).mediaUUID)
        .filter((uuid): uuid is string => uuid != null)
    );
  });

  function clearMediumData(data: { id: number }) {
    const medium = items.value[data.id];
    if (!medium) return;
    
    const mediumData = medium.data;
    const mediaUUID = mediumData.mediaUUID;
    if (!mediaUUID) return;

    sceneNodeStore.update({
      id: data.id,
      data: {
        ...mediumData,
        mediaUUID: undefined
      }
    });

    // Does any other medium still use this media?
    if (!linkedMediaUUIDs.value.has(mediaUUID)) {
      useMediaStore().delete({ uuid: mediaUUID });
    } else {
      console.log("don't delete medium, still in use");
    }
  }

  function $reset() {
    // handled by sceneNodeStore.
    return;
  }

  return {
    create,
    items,
    byUid,
    mediaData,
    linkedMediaUUIDs,
    clearMediumData,
    $reset
  }
});
